import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { createSubRowSortingFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { Row } from '@tanstack/react-table';

import { TableCellDisplay } from '../../ui-v2/inputs/table/table-integer-display';
import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

const useColumnDefShopifyVariantId =
  (): ExtendedColumnDef<DealItemWithVersions> => {
    const { t } = useTranslation();
    const { setColumnSort } = useSetColumnSort();

    const accessorKey = 'shopifyVariantId';

    const getValue = (row: Row<DealItemWithVersions>) => {
      const value = row.original.activeVersion.shopifyVariantId;
      if (typeof value === 'undefined' || value === null) return null;
      return value;
    };

    const getParentValue = (row: Row<DealItemWithVersions>) => {
      const subRowsValues: string[] = [];
      row.subRows.forEach((subRow) => {
        const value = subRow.original.activeVersion.shopifyVariantId;
        if (typeof value !== 'undefined' && value !== null) {
          subRowsValues.push(value);
        }
      });

      if (subRowsValues.length === 0) {
        return null;
      }

      const firstValue = subRowsValues[0];
      const allSame = subRowsValues.every((val) => val === firstValue);
      return allSame ? firstValue : null;
    };

    return useMemo(
      () => ({
        accessorKey: accessorKey,
        name: 'Shopify Variant ID',
        sortingFn: createSubRowSortingFn(
          accessorKey,
          (row) => row.activeVersion?.shopifyVariantId ?? null
        ),
        header: () => (
          <div
            className="flex flex-1 items-center gap-2"
            onClick={() => setColumnSort(accessorKey as keyof DealItem)}
          >
            {t('kpi.shopify_variant_id', 'Shopify Variant ID')}
            <SortIcon pointer={accessorKey as keyof DealItem} />
          </div>
        ),
        cell: ({ row }) => {
          const isParent = row.subRows.length > 1;
          const displayValue = isParent ? getParentValue(row) : getValue(row);
          const isEmpty = !displayValue;

          return (
            <TableCellWithRecentlyModified
              productId={row.original.id}
              field="shopifyVariantId"
              isParent={isParent}
            >
              {(isRecentlyModified) => (
                <div className="h-full w-full">
                  <TableCellDisplay
                    value={displayValue}
                    isEmpty={isEmpty}
                    isRecentlyModified={isRecentlyModified}
                    isEditable={false}
                    isParent={isParent}
                  />
                </div>
              )}
            </TableCellWithRecentlyModified>
          );
        },
        size: 150,
      }),
      [t, setColumnSort]
    );
  };

export default useColumnDefShopifyVariantId;
